import React, { useState, useRef, useEffect } from "react";
import Icon from "./Icon";
//font awsome icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFolder } from "@fortawesome/free-solid-svg-icons";

function Folder({
  folderId,
  folderName,
  icons,
  onIconPositionChange,
  onIconSelect,
  onIconDoubleClick,
  onMoveIconToFolder,
  selectedIcons,
  dragState,
  windowSize = { width: 600, height: 400 },
}) {
  const GRID_SIZE = 90;
  const [folderSize, setFolderSize] = useState({
    width: windowSize.width - 40,
    height: windowSize.height - 80,
  });
  const [selectionBox, setSelectionBox] = useState(null);
  const folderRef = useRef(null);
  const [folderRect, setFolderRect] = useState(null);

  // Filter icons that belong to this folder
  const folderIcons = icons.filter((icon) => icon.parentFolder === folderId);

  // Calculate grid dimensions
  const gridCols = Math.floor(folderSize.width / GRID_SIZE);
  const gridRows = Math.floor(folderSize.height / GRID_SIZE);

  const gridToAbsolute = (gridPos) => ({
    x: gridPos.x * GRID_SIZE,
    y: gridPos.y * GRID_SIZE,
  });

  const absoluteToGrid = (absPos) => ({
    x: Math.round(absPos.x / GRID_SIZE),
    y: Math.round(absPos.y / GRID_SIZE),
  });

  const isValidGridPosition = (gridPos, excludeId = null) => {
    if (
      gridPos.x < 0 ||
      gridPos.y < 0 ||
      gridPos.x >= gridCols ||
      gridPos.y >= gridRows
    ) {
      return false;
    }

    return !folderIcons.some(
      (icon) =>
        icon.id !== excludeId &&
        icon.gridPosition.x === gridPos.x &&
        icon.gridPosition.y === gridPos.y
    );
  };

  const handleFolderMouseDown = (e) => {
    if (
      e.target === folderRef.current ||
      e.target.classList.contains("folder-content")
    ) {
      // Update folderRect right before selection/drag
      if (folderRef.current) {
        setFolderRect(folderRef.current.getBoundingClientRect());
      }
      const rect = folderRef.current.getBoundingClientRect();
      const startPoint = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };

      setSelectionBox({
        start: startPoint,
        current: startPoint,
      });

      // Clear selection when starting new selection box
      onIconSelect(null, false, true); // Clear all selections
    }
  };

  const handleFolderMouseMove = (e) => {
    if (!selectionBox) return;

    const rect = folderRef.current.getBoundingClientRect();
    const currentPoint = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };

    setSelectionBox((prev) => ({
      ...prev,
      current: currentPoint,
    }));
  };

  const handleFolderMouseUp = (e) => {
    if (!selectionBox) return;

    // Calculate which icons are within the selection box
    const boxLeft = Math.min(selectionBox.start.x, selectionBox.current.x);
    const boxRight = Math.max(selectionBox.start.x, selectionBox.current.x);
    const boxTop = Math.min(selectionBox.start.y, selectionBox.current.y);
    const boxBottom = Math.max(selectionBox.start.y, selectionBox.current.y);

    const selectedIconIds = [];
    folderIcons.forEach((icon) => {
      const iconPos = gridToAbsolute(icon.gridPosition);
      const iconLeft = iconPos.x;
      const iconRight = iconPos.x + GRID_SIZE;
      const iconTop = iconPos.y;
      const iconBottom = iconPos.y + GRID_SIZE;

      // Check if icon overlaps with selection box
      if (
        iconLeft < boxRight &&
        iconRight > boxLeft &&
        iconTop < boxBottom &&
        iconBottom > boxTop
      ) {
        selectedIconIds.push(icon.id);
      }
    });

    // Select the icons found in the selection box
    selectedIconIds.forEach((iconId) => {
      onIconSelect(iconId, true, false);
    });

    setSelectionBox(null);
  };

  const handleIconPositionChangeInFolder = (iconId, action, position) => {
    if (action === "endDrag") {
      const targetGrid = absoluteToGrid(position);
      if (isValidGridPosition(targetGrid, iconId)) {
        onIconPositionChange(iconId, action, position, folderId);
      } else {
        // Reset to original position
        const icon = icons.find((i) => i.id === iconId);
        if (icon) {
          const originalAbs = gridToAbsolute(icon.gridPosition);
          onIconPositionChange(iconId, action, originalAbs, folderId);
        }
      }
    } else {
      onIconPositionChange(iconId, action, position, folderId);
    }
  };

  // Handle drag and drop from outside the folder
  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();

    // Get the dragged icon id from the drag event
    const draggedIconId = e.dataTransfer.getData("text/plain");

    if (draggedIconId && onMoveIconToFolder) {
      // Find an available position in the folder
      let newPosition = { x: 0, y: 0 };

      // Find first available grid position
      for (let y = 0; y < gridRows; y++) {
        for (let x = 0; x < gridCols; x++) {
          if (isValidGridPosition({ x, y })) {
            newPosition = { x, y };
            break;
          }
        }
        if (
          newPosition.x !== 0 ||
          newPosition.y !== 0 ||
          isValidGridPosition({ x: 0, y: 0 })
        ) {
          break;
        }
      }

      onMoveIconToFolder(draggedIconId, folderId, newPosition);
    }
  };
  // Update folder size when window size changes
  useEffect(() => {
    setFolderSize({
      width: windowSize.width - 40,
      height: windowSize.height - 80,
    });
  }, [windowSize]);
  //put
  // Update folder rect on mount and resize
  useEffect(() => {
    if (folderRef.current) {
      setFolderRect(folderRef.current.getBoundingClientRect());
    }
    const handleResize = () => {
      if (folderRef.current) {
        setFolderRect(folderRef.current.getBoundingClientRect());
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [folderSize]);

  // Add selection box event listeners
  useEffect(() => {
    if (selectionBox) {
      document.addEventListener("mousemove", handleFolderMouseMove);
      document.addEventListener("mouseup", handleFolderMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleFolderMouseMove);
        document.removeEventListener("mouseup", handleFolderMouseUp);
      };
    }
  }, [selectionBox, folderIcons]);

  return (
    <div
      ref={folderRef}
      className="folder-content"
      onMouseDown={handleFolderMouseDown}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#3c3c3cff",
        border: "2px dashed transparent",
        transition: "border-color 0.2s ease",
      }}
      onDragEnter={(e) => {
        e.currentTarget.style.borderColor = "#007bff";
        e.currentTarget.style.backgroundColor = "#f0f8ff";
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          e.currentTarget.style.borderColor = "transparent";
          e.currentTarget.style.backgroundColor = "#f5f5f5";
        }
      }}
    >
      {/* Selection Box */}
      {selectionBox && (
        <div
          style={{
            position: "absolute",
            left: `${Math.min(selectionBox.start.x, selectionBox.current.x)}px`,
            top: `${Math.min(selectionBox.start.y, selectionBox.current.y)}px`,
            width: `${Math.abs(
              selectionBox.current.x - selectionBox.start.x
            )}px`,
            height: `${Math.abs(
              selectionBox.current.y - selectionBox.start.y
            )}px`,
            backgroundColor: "rgba(0, 123, 255, 0.2)",
            border: "1px solid #007bff",
            pointerEvents: "none",
            zIndex: 999,
          }}
        />
      )}

      {/* Folder Icons */}
      {folderIcons.map((icon) => {
        const absolutePos = gridToAbsolute(icon.gridPosition);
        const dragInfo = dragState[icon.id];
        return (
          <Icon
            key={`folder-${folderId}-icon-${icon.id}`}
            id={icon.id}
            file_name={icon.file_name}
            icon_source={icon.icon_source}
            gridPosition={icon.gridPosition}
            absolutePosition={absolutePos}
            isDragging={dragInfo?.isDragging || false}
            isSelected={selectedIcons.has(icon.id)}
            onPositionChange={handleIconPositionChangeInFolder}
            onSelect={onIconSelect}
            onOpen={() => {}} // Handle in parent
            onDoubleClick={onIconDoubleClick}
            parentFolder={folderId}
            onMoveIconToFolder={onMoveIconToFolder}
            folderRect={folderRect}
            gridSize={GRID_SIZE}
            gridCols={gridCols}
            gridRows={gridRows}
            isValidGridPosition={isValidGridPosition}
            getContainerRect={() => folderRef.current.getBoundingClientRect()}
          />
        );
      })}

      {/* Empty folder message */}
      {folderIcons.length === 0 && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: "#666",
            fontSize: "16px",
            textAlign: "center",
            pointerEvents: "none",
            userSelect: "none",
          }}
        >
          <div style={{ fontSize: "48px", marginBottom: "10px" }}>
            <FontAwesomeIcon icon={faFolder} />
          </div>
          <div>This folder is empty</div>
          <div style={{ fontSize: "12px", marginTop: "5px" }}>
            Drag files here to add them
          </div>
        </div>
      )}
    </div>
  );
}

export default Folder;
